import { BookCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate, fmtMoney } from '../../shared/format'
import { OPENING_COUNTERS, useOpenings, usePostOpening, type OpeningRow } from '../pnl'
import { toastError } from '../ui'
import { Section } from './FinanceBits'

const yearStart = () => `${new Date().getFullYear()}-01-01`

/**
 * Para hesaplarının açılış bakiyeleri açılış fişiyle yevmiyeye yazılır (mizanda kasa/banka bakiyeleri açılıştan başlar).
 * Karşı hesap özkaynak ya da ortaklara borç; açılış bakiyesi sonradan değişirse "güncel değil" görünür ve yeniden yazılır
 * (eski fiş ters kayıtla kapanır). Döviz/altın hesabında açılış günü TCMB alış kuru kullanılır.
 */
export function OpeningsPanel({ canPost }: { canPost: boolean }) {
  const { t } = useTranslation()
  const q = useOpenings()
  const post = usePostOpening()
  const [date, setDate] = useState(yearStart)
  const [counter, setCounter] = useState<string>(OPENING_COUNTERS[0])
  const pending = (q.data ?? []).filter((x) => !x.posted || x.stale)
  const counterOptions = OPENING_COUNTERS.map((c) => ({ value: c, label: t(`finance.openings.counters.${c}`) }))

  const run = (row?: OpeningRow) =>
    void confirmModal({
      title: row ? t('finance.openings.confirmOne', { name: row.name }) : t('finance.openings.confirmAll', { count: pending.length }),
      description: t('finance.openings.confirmBody', { date: fmtDate(date), counter: t(`finance.openings.counters.${counter}`) }),
      confirmLabel: t('finance.openings.post'),
    }).then(
      (ok) =>
        ok &&
        post.mutate(
          { accountId: row?.accountId, date, counterCode: counter },
          { onSuccess: () => toast.success(t('finance.openings.posted')), onError: toastError },
        ),
    )

  return (
    <Section
      title={t('finance.openings.title')}
      icon={BookCheck}
      bodyClassName="p-0"
      actions={
        canPost ? (
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-40">
              <DateField id="op-date" aria-label={t('finance.openings.date')} value={date} onChange={setDate} clearable={false} />
            </div>
            <Select className="w-56" aria-label={t('finance.openings.counter')} value={counter} onChange={setCounter} options={counterOptions} />
            <Button size="sm" disabled={!pending.length || !date || post.isPending} onClick={() => run()}>
              {t('finance.openings.postAll', { count: pending.length })}
            </Button>
          </div>
        ) : null
      }
    >
      <p className="text-muted-foreground border-b px-4 py-2 text-xs">{t('finance.openings.hint')}</p>
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Skeleton className="m-4 h-32" />
      ) : !q.data.length ? (
        <EmptyState icon={BookCheck} title={t('finance.openings.empty')} />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <TR>
                <TH>{t('finance.openings.account')}</TH>
                <TH className="text-right">{t('finance.openings.opening')}</TH>
                <TH>{t('finance.openings.status')}</TH>
                <TH>{t('finance.openings.date')}</TH>
                <TH>{t('finance.openings.counter')}</TH>
                <TH className="text-right">{t('finance.openings.amountTry')}</TH>
                {canPost ? <TH /> : null}
              </TR>
            </THead>
            <TBody>
              {q.data.map((r) => (
                <TR key={r.accountId}>
                  <TD>
                    <p className="font-medium">{r.name}</p>
                    <p className="text-muted-foreground text-xs">{t(`finance.accountType.${r.type}`, { defaultValue: r.type })}</p>
                  </TD>
                  <TD className="text-right tabular-nums">{fmtMoney(r.opening, r.currency)}</TD>
                  <TD>
                    {!r.posted ? (
                      <Badge tone="warning">{t('finance.openings.notPosted')}</Badge>
                    ) : r.stale ? (
                      <Badge tone="danger">{t('finance.openings.stale')}</Badge>
                    ) : (
                      <Badge tone="success">{t('finance.openings.ok')}</Badge>
                    )}
                  </TD>
                  <TD className="tabular-nums">{r.date ? fmtDate(r.date) : '—'}</TD>
                  <TD>{r.counterCode ? t(`finance.openings.counters.${r.counterCode}`) : '—'}</TD>
                  <TD className="text-right tabular-nums">
                    {r.amountTry ? fmtMoney(r.amountTry) : '—'}
                    {r.fxRate ? <span className="text-muted-foreground block text-xs">{t('finance.openings.rate', { rate: r.fxRate })}</span> : null}
                  </TD>
                  {canPost ? (
                    <TD className="text-right">
                      <Button size="sm" variant="outline" disabled={post.isPending || !date} onClick={() => run(r)}>
                        {r.posted ? t('finance.openings.repost') : t('finance.openings.post')}
                      </Button>
                    </TD>
                  ) : null}
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </Section>
  )
}
